// Shared helpers for the Heshboniot Israel (Israel Tax Authority / שע"מ) integration:
// the OAuth authorize URL, allocation-request API calls, and the re-authorization popup.
// Used by the 90-day expiry banner (App.vue), the failure popup (Feature 3), and the
// retroactive allocation flow (Feature 1). NOTE: no optional chaining (?.) - not supported
// by the current Vue CLI/Babel config.

import axiosV2, { apiPost, apiRequest } from './http/axios_api_v2';

export const HESHBONIOT_ISRAEL_CLIENT_ID = '58ccdc52d0258281ae95f83214028ee5';
export const HESHBONIOT_ISRAEL_REDIRECT_URI = 'https://planup.co.il/apiv2/heshboniotisrael/oAuthRedirect';

// Statuses returned by the backend approveInvoice / requestAllocation flow
// (mirror of HeshboniotisraelService constants).
export const HESHBONIOT_STATUS = {
    APPROVED: 1,
    DELAYED: 2,
    ERROR: 3,
    DELAYED_NO_ACTION_NEEDED: 4,
    AUTH_ERROR: 5,
};

// The scope of the consent = the api's HeshboniotisraelService.scope, which the code is exchanged
// with: the Tax Authority's invoices family since 1/11/2026 (the old `scope=scope` is refused, and a
// consent given with it cannot be refreshed into the new one - the business connects once more).
export const HESHBONIOT_ISRAEL_SCOPE = 'invoices_scope';

// Sending the browser here starts the Tax Authority (re-)authorization OAuth flow.
export function getTaxAuthorityAuthorizeUrl() {
    return 'https://openapi.taxes.gov.il/shaam/production/longtimetoken/oauth2/authorize' +
        '?response_type=code&client_id=' + HESHBONIOT_ISRAEL_CLIENT_ID +
        '&scope=' + HESHBONIOT_ISRAEL_SCOPE + '&redirect_uri=' + HESHBONIOT_ISRAEL_REDIRECT_URI;
}

// $swal is sweetalert2 6: a confirmed popup resolves `true` (not the v7+ {value} / v9+ {isConfirmed}
// object), and a dismissed one REJECTS - so await it with `.catch(() => false)` and test the result
// here. Testing result.value alone made every confirm button of these flows a dead click
// (פפריקה, 15/10/2026: "ביטול הבקשה ומחיקת החשבונית" did nothing).
export function swalConfirmed(result) {
    return result === true || !!(result && (result.isConfirmed || result.value));
}

// Feature 3: the popup of a refused authorization. It asks the api first whether reconnecting can
// help (check-connection): when the Tax Authority refuses a token of the current scope that it has
// just issued (invalid_scope), a new consent gets the same refusal. Otherwise the re-register CTA -
// saying why when the consent predates the Tax Authority's new scope (reason 'scope': from 1/11/2026
// every business connected before then connects once more).
export async function showTaxAuthorityReauthPopup(swal) {
    swal({ title: 'בודקים את החיבור לרשות המסים...', allowOutsideClick: false, allowEscapeKey: false, confirmButtonColor: '#ab8ce4' }).catch(() => { });
    if (swal.showLoading) swal.showLoading();

    let check = null;
    try {
        check = await checkTaxAuthorityConnection();
    } catch (e) { /* no answer - the reconnect popup, as before */ }

    if (check && check.status === 'refused')
        return swal({
            title: 'רשות המסים חוסמת כרגע את הגישה',
            html: 'רשות המסים דוחה את הבקשות של PlanUp גם עם חיבור תקף' + (check.oauthErrorDescription ? ' (' + escapeHtml(check.oauthErrorDescription) + ')' : '') +
                '. זו לא בעיה בחיבור שלכם, ולכן התחברות מחדש לא תפתור אותה.<br>' +
                'חשבוניות שמופקות בינתיים יוצאות בלי מספר הקצאה, ואפשר לבקש עבורן מספר הקצאה מתוך המסמך כשהגישה תחזור. לבירור: התמיכה של PlanUp.',
            type: 'error',
            confirmButtonText: 'הבנתי',
            confirmButtonColor: '#ab8ce4',
        }).catch(() => { });

    if (check && check.status === 'ok')
        return swal({
            title: 'החיבור לרשות המסים תקין',
            html: 'רשות המסים לא אישרה את הבקשה הקודמת, אבל כרגע החיבור תקין.<br>אפשר לנסות שוב.',
            type: 'info',
            confirmButtonText: 'הבנתי',
            confirmButtonColor: '#ab8ce4',
        }).catch(() => { });

    return swal({
        title: 'נדרשת התחברות מחדש לרשות המסים',
        html: check && check.reason === 'scope'
            ? 'רשות המסים שינתה ב-1/11/2026 את אופן החיבור של תוכנות להנהלת חשבונות, וההרשאה שניתנה לפני השינוי כבר לא מתקבלת.<br>יש להתחבר מחדש פעם אחת כדי להמשיך לקבל מספרי הקצאה.'
            : 'ההרשאה מול רשות המסים פגה או אינה תקפה, ולכן לא ניתן לקבל מספר הקצאה לחשבונית.<br>יש להתחבר מחדש כדי להמשיך.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'התחבר מחדש לרשות המסים',
        cancelButtonText: 'סגור',
        confirmButtonColor: '#ab8ce4',
    }).then(result => {
        if (swalConfirmed(result))
            window.location.href = getTaxAuthorityAuthorizeUrl();
    }, () => { /* dismissed */ });
}

// A tax document is "held" (מעוכבת) awaiting a decision when the Tax Authority returned code 461
// for it and no allocation number was granted since. Only in this state does the TA decision API
// (cancel / continue / reverse charge / hearing) apply - it is the state users previously could
// only resolve through the Tax Authority website if they missed the popup at creation time.
export function isHeshbonitAwaitingDelayDecision(taxDocument) {
    if (!taxDocument) return false;
    if (['heshbonitMas', 'heshbonitMasKabala'].indexOf(taxDocument.type) === -1) return false;
    if (taxDocument.heshboniotIsraelAllocationNumber) return false;
    if (taxDocument.heshboniotIsraelContinuedWithoutAllocation) return false;
    // Reverse-charged: the held request was resolved by the zero-VAT re-issue (action=3).
    if (taxDocument.heshboniotIsraelReversedByDocId) return false;
    const err = taxDocument.heshboniotIsraelError;
    const errors = err && err.message && err.message.errors;
    return !!(Array.isArray(errors) && errors[0] && errors[0].code == 461);
}

function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
}

// A request the Tax Authority never received (mirror of the api's neverReachedTaxAuthority): its
// gateway turned the call away (a 4xx in its { httpCode } shape - 401: the token was refused), the
// token endpoint refused, or our own checks stopped it. Its cancel deletes the invoice without
// asking the Tax Authority - nothing is open there.
export function allocationRequestNeverSent(taxDocument) {
    const err = taxDocument && taxDocument.heshboniotIsraelError;
    if (!err || typeof err !== 'object') return false;
    const gatewayCode = +err.httpCode;
    if (gatewayCode >= 400 && gatewayCode < 500) return true;
    if (typeof err.error === 'string') return true;
    return typeof err.message === 'string'
        && (err.message.indexOf('No Heshboniot Israel refresh token stored') === 0 || err.message.indexOf('לא ניתן לבקש מספר הקצאה') === 0);
}

// A request that was attempted and FAILED (auth/validation/transport error) - as opposed to held
// (461, which has the full decision flow) or approved. Such requests can be cancelled: the TA
// holds nothing open for them (a request it never received is not even asked; for the rest its
// Cancel answers 463 = nothing to cancel, treated as confirmed).
export function hasFailedAllocationRequest(taxDocument) {
    if (!taxDocument) return false;
    if (['heshbonitMas', 'heshbonitMasKabala'].indexOf(taxDocument.type) === -1) return false;
    if (taxDocument.heshboniotIsraelAllocationNumber) return false;
    if (taxDocument.heshboniotIsraelContinuedWithoutAllocation) return false;
    if (taxDocument.heshboniotIsraelReversedByDocId) return false;
    const err = taxDocument.heshboniotIsraelError;
    if (!err) return false;
    if (isHeshbonitAwaitingDelayDecision(taxDocument)) return false;
    // 462 = the TA decided no allocation number is required - nothing wrong, nothing to cancel.
    const errors = err && err.message && err.message.errors;
    if (Array.isArray(errors) && errors[0] && errors[0].code == 462) return false;
    return true;
}

// The Tax Authority's answer codes that have a sentence of their own (mirror of the api's
// heshboniotErrorReason).
const TAX_AUTHORITY_CODE_TEXT = {
    461: 'רשות המסים עיכבה את מתן מספר ההקצאה - נדרשת בדיקה או פעולה מולה.',
    462: 'רשות המסים קבעה שלא נדרש מספר הקצאה לחשבונית זו.',
    432: 'מספר ת.ז / ח.פ של הלקוח שגוי - רשות המסים דחתה את הבקשה (קוד 432). יש לתקן את המספר בכרטיס הלקוח ולבקש מספר הקצאה שוב מתוך המסמך.',
};

// Human-readable Hebrew reason for a request that did not end in an allocation number, from the
// stored TA error blob: the TA's own error list (with the known codes in Hebrew), a sentence the
// api stored itself ({message: '...'}, e.g. a missing customer id), a gateway blob (401), or a
// token error. Null when the blob says nothing.
export function heshbonitFailureReason(taxDocument) {
    const err = taxDocument && taxDocument.heshboniotIsraelError;
    if (!err) return null;
    if (typeof err === 'string') return err;
    const errors = err.message && err.message.errors;
    const first = Array.isArray(errors) ? errors[0] : null;
    if (first) {
        if (first.code && TAX_AUTHORITY_CODE_TEXT[first.code]) return TAX_AUTHORITY_CODE_TEXT[first.code];
        if (first.message) return '' + first.message + (first.param ? ' (שדה: ' + first.param + ')' : '') + (first.code ? ' [קוד ' + first.code + ']' : '');
        if (first.code) return 'שגיאה מרשות המסים (קוד ' + first.code + ').';
    }
    if (typeof err.message === 'string' && err.message) return err.message;
    // a refused authorization: the gateway's English sentence tells the user nothing, and the way out is theirs
    if (err.httpCode == 401 || err.httpCode == 403 || err.error === 'invalid_grant')
        return 'רשות המסים לא אישרה את החיבור של PlanUp לעסק (שגיאת הרשאה). יש להתחבר מחדש לרשות המסים ואז לבקש מספר הקצאה שוב מתוך המסמך.';
    if (err.error_description) return '' + err.error_description;
    if (err.error) return '' + err.error;
    if (err.moreInformation) return '' + err.moreInformation;
    if (err.httpMessage) return '' + err.httpMessage + (err.httpCode ? ' (' + err.httpCode + ')' : '');
    return null;
}

// Human-readable Hebrew reason for the hold, from the stored TA error blob (for the delayed-invoice modal).
export function heshbonitDelayReason(taxDocument) {
    return heshbonitFailureReason(taxDocument) || 'רשות המסים עיכבה את מתן מספר ההקצאה לחשבונית זו.';
}

// ---- API calls ----
export function getMissingAllocation() {
    return axiosV2('heshboniotisrael/missing-allocation');
}
export function getHeldInvoices() {
    return axiosV2('heshboniotisrael/held');
}
export function getAllocationRequests() {
    return axiosV2('heshboniotisrael/requests');
}
export function cancelAllocationRequest(taxDocumentId) {
    return apiPost('heshboniotisrael/cancel', { taxDocumentId }, true);
}
// { status: 'reconnect' | 'refused' | 'ok' | 'unknown', oauthError, oauthErrorDescription } - see the popup
// (and the settings card's "בדיקת החיבור"). It calls the Tax Authority: never from a page load
export function checkTaxAuthorityConnection() {
    return apiRequest('get', 'heshboniotisrael/check-connection', null, { showDefaultError: false }, true);
}
function decisionBulkRequest(taxDocumentIds, action) {
    // showDefaultError:false — the runner shows its own popups (see requestAllocationBulk).
    return apiRequest('post', 'heshboniotisrael/decision-bulk', { taxDocumentIds, action }, { showDefaultError: false }, true);
}
export function requestAllocation(taxDocumentId) {
    return apiPost('heshboniotisrael/request-allocation', { taxDocumentId }, true);
}
export function requestAllocationBulk(taxDocumentIds) {
    // showDefaultError:false — the callers show their own popups, and the global s_apiError
    // handler must not fire a second popup on top (e.g. on a gateway error with no JSON body).
    return apiRequest('post', 'heshboniotisrael/request-allocation-bulk', { taxDocumentIds }, { showDefaultError: false }, true);
}

// Feature 1 (core): send allocation requests for a specific set of tax-document IDs,
// handle the "authorization expired" case, and report the outcome. Shared by the
// bulk "do all" flow and the manual-select modal (Settings.vue).
// The server only processes what fits in its time budget per request (so the gateway never
// times out at 60s) and returns the rest in remainingIds — keep calling until the list is done.
export async function requestAllocationForIds(vm, ids) {
    if (!ids || !ids.length) return null;

    vm.$root.$emit('showSpinner');

    const totals = { total: ids.length, approved: 0, delayed: 0, errors: 0, authError: false, details: [] };
    let pending = ids;
    let transportError = false;

    while (pending && pending.length) {
        let result;
        try {
            result = await requestAllocationBulk(pending);
        } catch (e) {
            transportError = true;
            break;
        }

        totals.approved += (result && result.approved) || 0;
        totals.delayed += (result && result.delayed) || 0;
        totals.errors += (result && result.errors) || 0;
        if (result && result.details && result.details.length)
            totals.details = totals.details.concat(result.details);

        if (result && result.authError) {
            totals.authError = true;
            break;
        }

        const remaining = (result && result.remainingIds) || [];
        // The server always processes at least one document per call; if it reports no
        // progress something is off — bail out instead of looping forever.
        if (remaining.length >= pending.length) break;
        pending = remaining;
    }

    vm.$root.$emit('hideSpinner');

    if (totals.authError) {
        showTaxAuthorityReauthPopup(vm.$swal);
        return totals;
    }

    const doneCount = totals.approved + totals.delayed + totals.errors;

    if (transportError && !doneCount) {
        vm.$swal({ title: 'שגיאה', text: 'אירעה שגיאה בשליחת הבקשות', type: 'error', confirmButtonColor: '#ab8ce4' });
        return null;
    }

    // Surface the first failure reason (if any) so the user understands why some didn't go through.
    let firstErrorReason = '';
    if (totals.details.length) {
        const failed = totals.details.find(d => d.reason && d.status !== HESHBONIOT_STATUS.APPROVED);
        if (failed) firstErrorReason = failed.reason;
    }

    vm.$swal({
        title: transportError ? 'הבקשות הושלמו חלקית' : 'הבקשות הושלמו',
        html: 'אושרו: ' + totals.approved + '<br>' +
            'נדחו/ממתינים: ' + totals.delayed + '<br>' +
            'נכשלו: ' + totals.errors +
            (transportError ? '<br><br>חלק מהבקשות לא נשלחו עקב שגיאת תקשורת — ניתן ללחוץ שוב כדי להמשיך מהנקודה שבה נעצר.' : '') +
            (firstErrorReason ? ('<br><br><b>סיבה לדוגמה:</b> ' + firstErrorReason) : ''),
        type: transportError ? 'warning' : 'success',
        confirmButtonColor: '#ab8ce4',
    });

    return totals;
}

// Bulk-run one decision (cancel / continue / furtherObjection) for the given held invoices.
// Mirrors requestAllocationForIds: the server only processes what fits its time budget per
// request (so the gateway never times out) and returns the rest in remainingIds — keep calling
// until the list is done. Returns the totals (or null on a pure transport failure).
export async function runHeldDecisionForIds(vm, ids, action) {
    if (!ids || !ids.length) return null;

    vm.$root.$emit('showSpinner');

    const totals = { total: ids.length, succeeded: 0, warnings: 0, failed: 0, authError: false, details: [] };
    let pending = ids;
    let transportError = false;

    while (pending && pending.length) {
        let result;
        try {
            result = await decisionBulkRequest(pending, action);
        } catch (e) {
            transportError = true;
            break;
        }

        totals.succeeded += (result && result.succeeded) || 0;
        totals.warnings += (result && result.warnings) || 0;
        totals.failed += (result && result.failed) || 0;
        if (result && result.details && result.details.length)
            totals.details = totals.details.concat(result.details);

        if (result && result.authError) {
            totals.authError = true;
            break;
        }

        const remaining = (result && result.remainingIds) || [];
        // The server always processes at least one document per call; if it reports no
        // progress something is off — bail out instead of looping forever.
        if (remaining.length >= pending.length) break;
        pending = remaining;
    }

    vm.$root.$emit('hideSpinner');

    if (totals.authError) {
        showTaxAuthorityReauthPopup(vm.$swal);
        return totals;
    }

    const doneCount = totals.succeeded + totals.warnings + totals.failed;

    if (transportError && !doneCount) {
        vm.$swal({ title: 'שגיאה', text: 'אירעה שגיאה בשליחת הבקשות', type: 'error', confirmButtonColor: '#ab8ce4' });
        return null;
    }

    // Surface the first failure reason (if any) so the user understands what went wrong.
    let firstErrorReason = '';
    const failedDetail = totals.details.find(d => d.reason && !d.success);
    if (failedDetail) firstErrorReason = failedDetail.reason;

    const successLabels = {
        cancel: 'בוטלו ונמחקו',
        continue: 'הופקו ללא מספר הקצאה',
        furtherObjection: 'נשלחה עבורן בקשה לשימוע',
    };

    vm.$swal({
        title: transportError ? 'הפעולה הושלמה חלקית' : 'הפעולה הושלמה',
        html: (successLabels[action] || 'הצליחו') + ': ' + totals.succeeded + '<br>' +
            (totals.warnings ? ('נמחקו אך ללא אישור רשות המסים — מומלץ לוודא באזור האישי: ' + totals.warnings + '<br>') : '') +
            'נכשלו: ' + totals.failed +
            (transportError ? '<br><br>חלק מהבקשות לא נשלחו עקב שגיאת תקשורת — ניתן להריץ שוב כדי להמשיך מהנקודה שבה נעצר.' : '') +
            (firstErrorReason ? ('<br><br><b>סיבה לדוגמה:</b> ' + firstErrorReason) : ''),
        type: transportError ? 'warning' : 'success',
        confirmButtonColor: '#ab8ce4',
    });

    return totals;
}

// Feature 1 (bulk): fetch eligible docs, confirm, request for all, then report.
// `vm` is a component instance (needs $swal and $root for the spinner).
export async function runRetroAllocationBulk(vm) {
    vm.$root.$emit('showSpinner');

    let missing;
    try {
        missing = (await getMissingAllocation()).data;
    } catch (e) {
        vm.$root.$emit('hideSpinner');
        vm.$swal({ title: 'שגיאה', text: 'לא ניתן לטעון את רשימת החשבוניות', type: 'error', confirmButtonColor: '#ab8ce4' });
        return;
    }

    vm.$root.$emit('hideSpinner');

    const ids = ((missing && missing.taxDocuments) || []).map(d => d.id);

    if (!ids.length) {
        vm.$swal({ title: 'הכל מעודכן', text: 'אין חשבוניות הממתינות למספר הקצאה', type: 'success', confirmButtonColor: '#ab8ce4' });
        return;
    }

    const confirm = await vm.$swal({
        title: 'בקשת מספרי הקצאה',
        html: 'נמצאו ' + ids.length + ' חשבוניות ללא מספר הקצאה.<br>לשלוח בקשה לרשות המסים עבור כולן?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'שלח בקשות',
        cancelButtonText: 'ביטול',
        confirmButtonColor: '#ab8ce4',
    });

    if (!swalConfirmed(confirm)) return;

    return await requestAllocationForIds(vm, ids);
}

// Feature 1 (single doc / row): request one allocation number and route the outcome.
// Returns the backend result so the caller can update its own UI.
export async function runRetroAllocationSingle(vm, taxDocumentId) {
    vm.$root.$emit('showSpinner');
    let result;
    try {
        result = await requestAllocation(taxDocumentId);
    } catch (e) {
        vm.$root.$emit('hideSpinner');
        vm.$swal({ title: 'שגיאה', text: 'אירעה שגיאה בבקשת מספר הקצאה', type: 'error', confirmButtonColor: '#ab8ce4' });
        return null;
    }
    vm.$root.$emit('hideSpinner');

    const status = result && result.status;

    if (status === HESHBONIOT_STATUS.AUTH_ERROR) {
        showTaxAuthorityReauthPopup(vm.$swal);
    } else if (status === HESHBONIOT_STATUS.APPROVED) {
        vm.$swal({ title: 'התקבל מספר הקצאה', text: 'מספר הקצאה: ' + result.allocationNumber, type: 'success', confirmButtonColor: '#ab8ce4' });
    } else if (status === HESHBONIOT_STATUS.DELAYED) {
        vm.$swal({ title: 'החשבונית נדחתה', text: (result && result.reason) ? result.reason : 'רשות המסים עיכבה את מתן מספר ההקצאה. ניתן לטפל בכך ממסך החשבונית.', type: 'info', confirmButtonColor: '#ab8ce4' });
    } else if (status === HESHBONIOT_STATUS.DELAYED_NO_ACTION_NEEDED) {
        vm.$swal({ title: 'לא נדרש מספר הקצאה', text: (result && result.reason) ? result.reason : 'רשות המסים קבעה שלא נדרש מספר הקצאה לחשבונית זו.', type: 'info', confirmButtonColor: '#ab8ce4' });
    } else {
        vm.$swal({ title: 'שגיאה', html: 'לא ניתן היה לקבל מספר הקצאה עבור חשבונית זו.' + ((result && result.reason) ? ('<br><br><b>סיבה:</b> ' + result.reason) : ''), type: 'error', confirmButtonColor: '#ab8ce4' });
    }

    return result;
}
